import React, { useState, useEffect } from 'react';
import { devLog, devWarn, devError } from '../utils/devLogger';
import styled from 'styled-components';
import { FormTitle } from '../customComponents/FormTitle';
import { useLang } from '../context/LangContext';
import { formatPriceComponentsByLanguage } from '../utils/currency';

// MyBid와 동일한 컨테이너 구조
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 24px;
`;

const Content = styled.div`
  padding: 16px 0;
`;

// MyBid와 동일한 배경 컨테이너 (입찰이 있을 때와 동일한 색상 사용)
const TimerContainer = styled.div<{ $isWeb: boolean }>`
  background-color: #f8f5f0;
  border-radius: 12px;
  padding: 22px 16px;
  height: 171px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  
  @media (min-width: 768px) {
    background-color: ${props => props.$isWeb ? '#f8f5f0' : '#f8f5f0'};
  }
`;

// 원형 타이머 컨테이너
const CircularTimerContainer = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  cursor: pointer;
`;

const CircularProgress = styled.svg`
  transform: rotate(-90deg) scaleY(-1);
  width: 100%;
  height: 100%;
`;

const BackgroundCircle = styled.circle`
  fill: none;
  stroke: #eae0d1;
  stroke-width: 6.7;
`;

const ProgressCircle = styled.circle<{ $progress: number }>`
  fill: none;
  stroke: #7a6447;
  stroke-width: 6.7;
  stroke-linecap: round;
  stroke-dasharray: ${2 * Math.PI * 33.65};
  stroke-dashoffset: ${props => 2 * Math.PI * 33.65 * (1 - props.$progress)};
  transition: stroke-dashoffset 1s ease;
`;

const TimerContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: white;
  border-radius: 50%;
  margin: 6.7px;
`;

const SecondsText = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #ed2424;
  line-height: 1;
`;

const SecLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #ed2424;
  line-height: 1;
`;

// MyBid와 동일한 현재가 표시 영역 (row 구조)
const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

// MyBid와 동일한 현재가 라벨 스타일
const CurrentPriceLabel = styled.div`
  font-size: 14px;
  color: #b6a488;
  font-weight: 500;
`;

// MyBid와 동일한 금액 컨테이너
const PriceAmount = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

// MyBid와 동일한 환종 스타일
const Currency = styled.span`
  font-size: 12px;
  color: #b6a488;
  font-weight: 500;
`;

// MyBid와 동일한 금액 스타일
const Amount = styled.span`
  font-size: 16px;
  color: #7a6447;
  font-weight: 600;
`;

interface CountdownStatus {
  isRunning: boolean;
  isPaused: boolean;
  remainingTime: number;
  lotIndex?: number;
}

interface AuctionTimerProps {
  currentPrice?: number;
  isWeb?: boolean;
  onTimerClick?: () => void;
  exchangeRate?: number;
  countdownStatus?: CountdownStatus; // 소켓에서 받은 카운트다운 상태
}

// 다국어 텍스트
const auctionTimerTexts = {
  ko: {
    title: '경매 카운트',
    currentPrice: '현재가'
  },
  en: {
    title: 'Auction Count',
    currentPrice: 'Current Price'
  }
};

const AuctionTimer: React.FC<AuctionTimerProps> = ({ 
  currentPrice = 1000000, 
  isWeb = false,
  onTimerClick,
  exchangeRate = 1300,
  countdownStatus
}) => {
  const { lang } = useLang();
  const texts = auctionTimerTexts[lang];
  
  // 로컬 상태 (테스트용 - 소켓 데이터가 없을 때만 사용)
  const [localSeconds, setLocalSeconds] = useState(10);
  const [localIsRunning, setLocalIsRunning] = useState(false);

  // 소켓에서 온 데이터가 있으면 그것을 우선 사용, 없으면 로컬 상태 사용
  const seconds = countdownStatus?.remainingTime ?? localSeconds;
  
  // 디버그 로그 - 소켓 데이터 수신 확인
  useEffect(() => {
    if (countdownStatus) {
      devLog('⏰ [AuctionTimer] 소켓 카운트다운 상태 수신:', {
        isRunning: countdownStatus.isRunning,
        isPaused: countdownStatus.isPaused,
        remainingTime: countdownStatus.remainingTime,
        lotIndex: countdownStatus.lotIndex,
        현재표시시간: seconds
      });
    }
  }, [countdownStatus, seconds]);
  
  // 총 시간 계산 (소켓 데이터가 처음 왔을 때 설정)
  const [totalSeconds, setTotalSeconds] = useState(10);
  
  useEffect(() => {
    if (countdownStatus?.isRunning && countdownStatus.remainingTime > 0) {
      // 소켓에서 새로운 카운트다운이 시작되면 총 시간 업데이트
      setTotalSeconds(prev => Math.max(countdownStatus.remainingTime, prev));
    }
  }, [countdownStatus?.isRunning, countdownStatus?.remainingTime]);

  // 로컬 타이머 로직 (소켓 데이터가 없을 때만 사용)
  useEffect(() => {
    if (!countdownStatus) {
      let interval: NodeJS.Timeout;
      
      if (localIsRunning && localSeconds > 0) {
        interval = setInterval(() => {
          setLocalSeconds(prev => {
            if (prev <= 1) {
              setLocalIsRunning(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [localIsRunning, localSeconds, countdownStatus]);

  // 소켓에서 카운트다운 상태가 없거나 실행 중이 아니면 컴포넌트를 숨김
  if (!countdownStatus || !countdownStatus.isRunning) {
    devLog('⏰ [AuctionTimer] 타이머 숨김 - 소켓 데이터 없거나 실행 중 아님:', {
      hasCountdownStatus: !!countdownStatus,
      isRunning: countdownStatus?.isRunning,
      remainingTime: countdownStatus?.remainingTime
    });
    return null;
  }

  // 타이머 클릭 핸들러
  const handleTimerClick = () => {
    // 소켓 데이터가 없을 때만 로컬 테스트 타이머 실행
    if (!countdownStatus && !localIsRunning) {
      setLocalSeconds(10);
      setTotalSeconds(10);
      setLocalIsRunning(true);
    }
    
    devLog('⏰ [AuctionTimer] 타이머 클릭:', {
      hasSocketData: !!countdownStatus,
      socketIsRunning: countdownStatus?.isRunning,
      socketRemainingTime: countdownStatus?.remainingTime,
      localIsRunning,
      localSeconds
    });
    
    onTimerClick?.();
  };

  // 진행률 계산 (0에서 1 사이)
  const progress = totalSeconds > 0 ? seconds / totalSeconds : 0;

  // 원의 반지름 (80px 지름에서 stroke-width 고려)
  const radius = (80 - 6.7) / 2;

  // 언어별 가격 포맷팅
  const priceFormat = formatPriceComponentsByLanguage(currentPrice, exchangeRate, lang);

  return (
    <Container>
      <FormTitle>{texts.title}</FormTitle>
      <Content>
        <TimerContainer $isWeb={isWeb}>
          {/* 원형 타이머 */}
          <CircularTimerContainer onClick={handleTimerClick}>
            <CircularProgress>
              <BackgroundCircle
                cx="40"
                cy="40"
                r={radius}
              />
              <ProgressCircle
                cx="40"
                cy="40"
                r={radius}
                $progress={progress}
              />
            </CircularProgress>
            <TimerContent>
              <SecondsText>{seconds}</SecondsText>
              <SecLabel>SEC</SecLabel>
            </TimerContent>
          </CircularTimerContainer>
          
          {/* 현재가 표시 (MyBid와 동일한 구조) */}
          <PriceRow>
            <CurrentPriceLabel>{texts.currentPrice}</CurrentPriceLabel>
            <PriceAmount>
              <Currency>{priceFormat.currency}</Currency>
              <Amount>{priceFormat.amount}</Amount>
            </PriceAmount>
          </PriceRow>
        </TimerContainer>
      </Content>
    </Container>
  );
};

export default AuctionTimer;
